import settings from 'Cloud/settings';
import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import {
    GetExpiredAndroidSubscriptionAPICall,
    GetExpiredIosSubscriptionAPICall,
} from 'reactApp/api/billing/GetExpiredDeviceSubscriptionAPICall';
import { IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import { billingDataForExpiredEnabled } from 'reactApp/appHelpers/featuresHelpers/features/billingDataEndpoint';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { pastSubscriptionsRequest } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.module';
import { handlePastSubscriptionsSuccess } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.saga';
import { PastSubscriptionsSelectors } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.selectors';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage, isReactLandingPage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendXrayWithPlatform } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store } from 'reactApp/store';
import { renderMobileSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/helpers/SplashScreen.helpers';
import { useExpiredSubscriptionScreen } from 'reactApp/ui/Mobile/SplashScreen/hooks/useExpiredSubscriptionScreen';
import { put } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

const EXPIRED_DEVICE_SUBSCRIPTION_PROMO_ID = 'ExpiredDeviceSubscriptionPromo';
const CHECK_EXPIRED_SUBS_DATE = 'checkExpiredDeviceDate';
const EXPIRED_SUBS_SCREEN = 'expiredSubsScreen';

// для мобильных подписок период иначе не достать, так как их тарифы не возвращаются в product/list
const getMobileProductPeriod = (productId: string) => {
    const matchRes = productId.match(/.*\.(.*)/);
    const period = matchRes?.[1];
    switch (period) {
        case 'yearly':
            return '1y';
        case 'monthly':
            return '1m';
        default:
            return;
    }
};

// показывем сплеш скрин 4 раза, каждые 5 дней
const canShowExpiredScreen = (): boolean => {
    const MAX_SHOW_COUNT = 4;

    const showCount = storeHelper.getValue(EXPIRED_SUBS_SCREEN);

    if (showCount >= MAX_SHOW_COUNT) {
        return false;
    }

    return !!(!showCount || storeHelper.isPassed(EXPIRED_SUBS_SCREEN, { daysCount: 5 }));
};

// делаем запрос за отмененной подпиской раз в месяц
const canCheckExpiredSubs = (): boolean => {
    const screenWasShown = storeHelper.getValue(EXPIRED_SUBS_SCREEN);

    // если баннер был когда-то показан, то больше не показываем
    if (screenWasShown) {
        return false;
    }

    return !!(!storeHelper.getValue(CHECK_EXPIRED_SUBS_DATE) || storeHelper.isPassed(CHECK_EXPIRED_SUBS_DATE, { daysCount: 30 }));
};

export function* initExpiredDeviceSubscriptionPromo() {
    const isOverQuota = yield* select(UserSelectors.isOverQuotaUser);
    const isPaidUser = yield* select(UserSelectors.isPaidUser);
    const isPhone = yield* select(EnvironmentSelectors.isPhone);
    const isWebview = yield* select(EnvironmentSelectors.isWebview);
    const isAnonymous = yield* select(UserSelectors.isAnonymous);
    const isBizUser = yield* select(UserSelectors.isBizUser);
    const storage = yield* select(getCurrentStorage);
    const isLP = isReactLandingPage();

    if (
        isAnonymous ||
        isOverQuota ||
        isPaidUser ||
        isBizUser ||
        !isPhone ||
        isWebview ||
        storage === EStorageType.subscriptions ||
        settings?.request?.action ||
        isLP
    ) {
        return;
    }

    const canShowScreen = canShowExpiredScreen();
    const canCheckSubs = canCheckExpiredSubs();

    if (!canShowScreen && !canCheckSubs) {
        return;
    }

    let period;
    let quota;
    if (billingDataForExpiredEnabled) {
        yield put(pastSubscriptionsRequest());
        yield handlePastSubscriptionsSuccess();

        const latestSub = yield* select(
            PastSubscriptionsSelectors.latestSubsByExpiresByPlatform,
            IS_IOS_MOBILE ? BillingDataPlatform.ios : BillingDataPlatform.android
        );

        period = getMobileProductPeriod(latestSub.productId);
        quota = latestSub?.space.original;
    } else {
        const apiCall = IS_IOS_MOBILE ? new GetExpiredIosSubscriptionAPICall() : new GetExpiredAndroidSubscriptionAPICall();
        sendXrayWithPlatform(['expired_request']);
        const { data } = yield* call(apiCall.makeRequest);

        period = data?.period;
        quota = data?.services?.quota;
    }

    storeHelper.markAsShown(CHECK_EXPIRED_SUBS_DATE);
    if (!quota) {
        return;
    }

    yield productsController.loadProducts();

    const product = yield select(
        ProductsSelectors.getExpiredDeviceProduct,
        quota < SPACE_LIST.gb_32 ? SPACE_LIST.gb_32 : quota,
        period || '1y'
    );

    if (!product) {
        return;
    }

    const countShow = storeHelper.getValue(EXPIRED_SUBS_SCREEN) || 0;

    yield put(
        addPromoToShowQueue({
            type: EPromoType.expiredDeviceSubscription,
            promoId: EXPIRED_DEVICE_SUBSCRIPTION_PROMO_ID,
            onShow: () => {
                store.dispatch(promoShown(EPromoType.expiredDeviceSubscription));
                storeHelper.markAsShown(EXPIRED_SUBS_SCREEN, countShow + 1);
            },
            onClose: () => {
                store.dispatch(removePromo(EPromoType.expiredDeviceSubscription));
            },
            product,
        })
    );
}

export function* showExpiredDeviceSubscriptionPromo() {
    const expiredDeviceSubscriptionPromo = yield* select(PromoSelectors.getPromo(EPromoType.expiredDeviceSubscription));

    if (!expiredDeviceSubscriptionPromo) {
        return;
    }

    expiredDeviceSubscriptionPromo.onShow();

    yield call(
        renderMobileSplashScreen,
        { contentHook: useExpiredSubscriptionScreen, hookParams: { productId: expiredDeviceSubscriptionPromo.product?.id } },
        expiredDeviceSubscriptionPromo.onClose
    );
}
